import React, {useEffect, useState} from "react";
import axios from "axios";
import MUIDataTable from "mui-datatables";
import {
  Button,
  IconButton,
  Chip,
  Box,
  Typography,
  Paper,
  ThemeProvider,
  createMuiTheme,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
} from "@material-ui/icons";
import AddWorkshopDialog from "./AddWorkshopDialog";
import AdminLayout from "../../../core/AdminLayout";
import {ToastContainer, toast} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

const theme = createMuiTheme({
  palette: {
    primary: {
      main: "#2d3748",
      light: "#4a5568",
      dark: "#1a202c",
    },
    secondary: {
      main: "#f59e0b",
      light: "#fbbf24",
      dark: "#d97706",
    },
    background: {
      default: "#f7fafc",
      paper: "#ffffff",
    },
  },
  typography: {
    fontFamily: '"IBM Plex Sans", "Helvetica Neue", Arial, sans-serif',
    h4: {
      fontWeight: 700,
      letterSpacing: "-0.02em",
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    "none",
    "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  ],
});

export default function WorkshopList() {
  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const API_URL = window?.location?.hostname?.includes("maroc")
    ? process.env.REACT_APP_API_MAROC
    : process.env.REACT_APP_API;

  const fetchWorkshops = () => {
    setLoading(true);
    axios
      .get(`${API_URL}/workshops`)
      .then((res) => {
        setData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        toast.error("Failed to load workshops");
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchWorkshops();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this workshop?")) {
      try {
        await axios.delete(`${API_URL}/workshops/${id}`);
        toast.success("Workshop deleted successfully");
        fetchWorkshops();
      } catch (err) {
        toast.error("Failed to delete workshop");
      }
    }
  };

  const columns = [
    {
      name: "title",
      label: "Workshop Title",
      options: {
        customBodyRender: (value) => (
          <Typography variant="subtitle2" style={{fontWeight: 600}}>
            {value}
          </Typography>
        ),
      },
    },
    {
      name: "date",
      label: "Date",
      options: {
        customBodyRender: (value) => (
          <Box display="flex" alignItems="center" gap={1}>
            <EventIcon style={{fontSize: 18, color: "#718096"}} />
            <Typography variant="body2">{value}</Typography>
          </Box>
        ),
      },
    },
    {
      name: "participants_count",
      label: "Participants",
      options: {
        customBodyRender: (value) => (
          <Chip
            label={`${value || 0} participants`}
            color={value > 0 ? "primary" : "default"}
            size="small"
            style={{fontWeight: 500}}
          />
        ),
      },
    },
    {
      name: "id",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (id) => (
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<ViewIcon />}
              href={`/admin/workshops/${id}`}
              style={{textTransform: "none"}}
            >
              View Details
            </Button>
            <IconButton size="small" onClick={() => handleDelete(id)} style={{color: "#e53e3e"}}>
              <DeleteIcon />
            </IconButton>
          </Box>
        ),
      },
    },
  ];

  const options = {
    filterType: "checkbox",
    selectableRows: "none",
    elevation: 0,
    rowsPerPageOptions: [10, 25, 50],
    textLabels: {
      body: {
        noMatch: loading ? "Loading workshops..." : "No workshops found",
      },
    },
  };

  return (
    <ThemeProvider theme={theme}>
      <AdminLayout>
        <ToastContainer position="top-right" autoClose={3000} />

        <Box mb={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box>
              <Typography variant="h4" gutterBottom>
                Workshop Management
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Manage your workshops and generate certificates for participants
              </Typography>
            </Box>

            <Button
              variant="contained"
              color="secondary"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
              style={{
                textTransform: "none",
                fontWeight: 600,
                boxShadow: "0 4px 12px rgba(245, 158, 11, 0.3)",
              }}
            >
              Create Workshop
            </Button>
          </Box>

          <Paper elevation={2} style={{borderRadius: 12, overflow: "hidden"}}>
            <MUIDataTable data={data} columns={columns} options={options} />
          </Paper>
        </Box>

        <AddWorkshopDialog
          open={open}
          onClose={() => {
            setOpen(false);
            fetchWorkshops();
          }}
        />
      </AdminLayout>
    </ThemeProvider>
  );
}
